import Grid from '@mui/material/Grid';
import { Paragraph } from 'src/interviews/components/Layout/Embedded';
import Pill from './Pill';
import { formatLowerCase, formatSentenceCase } from 'src/interviews/utils/formatTitleCasing';

import type { IInterviewResults } from 'src/packages/polaris/types';

function Explanation({
  result,
  conditionName,
}: {
  result: IInterviewResults;
  conditionName: string;
}) {
  const diagnosis = result.diagnosis_of_concern || ({} as any);

  const hasCouldExplainList = !!diagnosis?.could_explain_list?.length;
  const hasDeniedCouldExplainList = !!diagnosis?.denied_could_explain_list?.length;
  const hasCantExplainList = !!diagnosis?.cant_explain_list?.length;

  const hasExplanation = hasCouldExplainList || hasDeniedCouldExplainList || hasCantExplainList;

  if (!hasExplanation) {
    return null;
  }

  const formattedDiagnosisName = formatLowerCase(conditionName);

  return (
    <Grid item className="bp-w-full">
      <Paragraph className="!bp-mb-3 bp-w-full bp-text-base">Here's why we think so:</Paragraph>

      <ul className="bp-ml-4">
        {hasCouldExplainList && (
          <li className="!bp-list-disc">
            <Paragraph className="bp-mb-3 bp-text-sm">
              You confirmed these top {formattedDiagnosisName} symptoms:
            </Paragraph>
            <div className="bp-flex bp-w-full bp-flex-wrap bp-gap-2 bp-pb-2">
              {diagnosis.could_explain_list.map((label: string) => (
                <Pill key={label} label={formatSentenceCase(label)} variant="check" />
              ))}
            </div>
          </li>
        )}
        {hasDeniedCouldExplainList && (
          <li className="!bp-list-disc">
            <Paragraph className="bp-my-3 bp-text-sm">
              {hasCouldExplainList
                ? `You did not report these other common symptoms of ${formattedDiagnosisName}`
                : `You didn't report any of these top ${formattedDiagnosisName} symptoms:`}
            </Paragraph>
            <div className="bp-flex bp-w-full bp-flex-wrap bp-gap-2 bp-pb-2">
              {diagnosis.denied_could_explain_list.map((label: string) => (
                <Pill key={label} label={formatSentenceCase(label)} variant="close" />
              ))}
            </div>
          </li>
        )}
        {hasCantExplainList && (
          <li className="!bp-list-disc">
            <Paragraph className="bp-my-3 bp-text-sm">
              {hasDeniedCouldExplainList || hasCouldExplainList
                ? `We also considered these symptoms you reported`
                : `You reported these symptoms`}{' '}
              that are typically unrelated to {formattedDiagnosisName}:
            </Paragraph>
            <div className="bp-flex bp-w-full bp-flex-wrap bp-gap-2 bp-pb-2">
              {diagnosis.cant_explain_list.map((label: string) => (
                <Pill key={label} label={formatSentenceCase(label)} variant="graycheck" />
              ))}
            </div>
          </li>
        )}
      </ul>
    </Grid>
  );
}

export default Explanation;
